import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CardComp, ModalCompAdvanceUpdate } from "../../../components";
import { ButtonComp } from "../../../components/Ui";
import { SettingSocietyBankInformationHeader } from "../../../utils/TableHeader";
import AllBankInfo from "./modules/AllBankInfo";
import {
  useDeleteBankInfoMutation,
  useGetAllBankInfoQuery,
  useGetBankUsageQuery,
  useConfigureBankRecipientMutation,
} from "../../../store/admin/settings/societyBankSlice";
import { toast } from "react-hot-toast";
import CreateBankInfo from "../../../components/society-banking/create-bank-info-modal";

/** User-facing copy avoids naming a payment provider; integration lives in the API (DB field may keep a legacy name). */

function DeleteBankModalBody({
  row,
  usage,
  banks,
  replacementId,
  setReplacementId,
  onConfirm,
  onCancel,
  isDeleting,
}) {
  const count = usage?.count ?? 0;
  const categories = usage?.categories ?? [];

  return (
    <div className="py-2">
      <h6 className="text-black2 fw-bold mb-2">Delete society bank</h6>
      {count > 0 ? (
        <>
          <p className="text-black2 fw-semibold mb-2 small">
            This account is linked to one or more contribution categories.
          </p>
          <p className="text-muted small mb-3">
            To delete it, choose another society bank account below. All listed
            contribution categories will be updated to use that account for
            payments.
          </p>
          {categories.length > 0 && (
            <ul className="small text-muted mb-3 border rounded py-2 px-3 bg-light">
              {categories.map((c) => (
                <li key={c.id}>{c.name || `Category #${c.id}`}</li>
              ))}
            </ul>
          )}
          <label className="form-label small fw-semibold text-black2">
            Replacement bank account
          </label>
          <select
            className="form-select mb-2"
            style={{ backgroundColor: "#fafafa" }}
            value={replacementId}
            onChange={(e) => setReplacementId(e.target.value)}
          >
            <option value="">Select bank</option>
            {banks
              .filter((b) => b.bank_id !== row?.bank_id)
              .map((b) => (
                <option key={b.bank_id} value={b.bank_id}>
                  {b.bank_name} — {b.account_number} ({b.account_name})
                </option>
              ))}
          </select>
          <p className="text-muted small mb-0">
            Selected categories will point to this account after you confirm.
          </p>
        </>
      ) : (
        <p className="text-muted small mb-0">
          Are you sure you want to delete this society bank account? This cannot
          be undone.
        </p>
      )}
      <div className="d-flex justify-content-end gap-2 mt-4">
        <ButtonComp
          btnText="Cancel"
          btnClassName="border border-primary1 text-primary1 px-4 py-2 rounded bg-white"
          onClick={onCancel}
        />
        <ButtonComp
          btnText={isDeleting ? "Deleting…" : "Delete account"}
          btnClassName="bg-danger text-white px-4 py-2 rounded border-0"
          onClick={onConfirm}
          disabled={isDeleting || (count > 0 && !replacementId)}
        />
      </div>
    </div>
  );
}

export default function SocietyBankInfo() {
  const navigate = useNavigate();
  const [modalPage, setModalPage] = useState("");
  const { error: errorBankInfo, data: bankListData } = useGetAllBankInfoQuery({
    pageNumber: 1,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [replacementId, setReplacementId] = useState("");

  const bankIdForUsage = deleteTarget?.bank_id;
  const { data: usageData, isFetching: usageLoading } = useGetBankUsageQuery(
    bankIdForUsage,
    { skip: !bankIdForUsage || modalPage !== "Delete Bank" }
  );

  const [deleteBankInfo, { isLoading: isDeleting }] = useDeleteBankInfoMutation();
  const [configureRecipient, { isLoading: configuring }] =
    useConfigureBankRecipientMutation();

  const openCreateBankModal = () => setModalPage("Create Bank Information");

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget?.bank_id) return;
    const count = usageData?.count ?? 0;
    if (count > 0 && !replacementId) {
      toast.error("Select a replacement bank account.");
      return;
    }
    const toastId = toast.loading("Deleting…");
    try {
      const payload = await deleteBankInfo({
        id: deleteTarget.bank_id,
        replacement_company_bank_id:
          count > 0 ? parseInt(replacementId, 10) : undefined,
      }).unwrap();
      if (payload?.status === false) {
        const errMsg =
          payload?.message || payload?.data?.message || "Request failed";
        toast.error(errMsg, { id: toastId });
        return;
      }
      toast.success("Bank account deleted.", { id: toastId });
      setModalPage("");
      setDeleteTarget(null);
      setReplacementId("");
    } catch (error) {
      if (error?.status === 401) {
        toast.error("Login to continue", { id: toastId });
        navigate("/login");
        return;
      }
      toast.error(
        error?.data?.message || error?.message || "Could not delete bank.",
        { id: toastId }
      );
    }
  }, [
    deleteBankInfo,
    deleteTarget,
    navigate,
    replacementId,
    usageData?.count,
  ]);

  const onCancelDelete = useCallback(() => {
    setModalPage("");
    setDeleteTarget(null);
    setReplacementId("");
  }, []);

  const handleConfigure = useCallback(
    async (row) => {
      if (row?.paystack_recipient_code) {
        toast.success("Payout recipient is already configured.");
        return;
      }
      const tid = toast.loading("Configuring…");
      try {
        const res = await configureRecipient(row.bank_id).unwrap();
        if (res?.status === false) {
          toast.error(res?.message || "Failed", { id: tid });
          return;
        }
        toast.success(res?.message || "Recipient configured.", { id: tid });
      } catch (err) {
        toast.error(
          err?.data?.message || err?.message || "Could not configure recipient.",
          { id: tid }
        );
      }
    },
    [configureRecipient]
  );

  const TableDropDown = [
    {
      name: "Configure payout recipient",
      action: (data) => handleConfigure(data),
    },
    {
      name: "Delete account",
      action: (data) => {
        setDeleteTarget(data);
        setReplacementId("");
        setModalPage("Delete Bank");
      },
    },
  ];

  const useModal = useMemo(
    () => [
      {
        name: "Delete Bank",
        component: (
          <div>
            {usageLoading && bankIdForUsage ? (
              <p className="text-muted small py-3">Loading…</p>
            ) : (
              <DeleteBankModalBody
                row={deleteTarget}
                usage={usageData}
                banks={bankListData?.company_banks ?? []}
                replacementId={replacementId}
                setReplacementId={setReplacementId}
                onConfirm={handleDeleteConfirm}
                onCancel={onCancelDelete}
                isDeleting={isDeleting}
              />
            )}
          </div>
        ),
      },
      {
        name: "Create Bank Information",
        component: <CreateBankInfo setModalPage={setModalPage} />,
      },
    ],
    [
      deleteTarget,
      usageData,
      usageLoading,
      bankIdForUsage,
      bankListData,
      replacementId,
      isDeleting,
      handleDeleteConfirm,
      onCancelDelete,
    ]
  );

  if (errorBankInfo && errorBankInfo.status === 401) {
    navigate("/login");
  }

  return (
    <section className="px-2 px-lg-4 min-vh-100">
      <div className="py-1"></div>
      <CardComp cardBodyClassName={"py-0"} cardClassName={"pt-4"}>
        <h4 className=" text-black1 fw-bold" data-tour="staff-settings-header">
          List of Society Bank Information
        </h4>
      </CardComp>
      <div className="w-full rounded-[20px] py-10 bg-white">
        <div className="w-full flex justify-end pr-10">
          <button
            className="w-[200px] bg-new-primary h-[50px] rounded-[10px] text-white"
            onClick={openCreateBankModal}
            type="button"
            data-tour="staff-settings-add"
          >
            Create new account
          </button>
        </div>
        <div data-tour="staff-settings-table">
        <AllBankInfo
          tableColumns={SettingSocietyBankInformationHeader(TableDropDown)}
          pageToggle={openCreateBankModal}
        />
        </div>
      </div>

      <ModalCompAdvanceUpdate
        pageName={modalPage}
        arrayComp={useModal}
        handleClose={() => {
          setModalPage("");
          setDeleteTarget(null);
          setReplacementId("");
        }}
        centered
        size={"md"}
      />
      {configuring ? <span className="d-none">busy</span> : null}
    </section>
  );
}
